import * as Yup from 'yup';
import { getAgeFromPersonalID } from 'utils/getAgeFromPersonalID';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { validatePersonalID } from 'validators/validatePersonalID';
import domainConfig from 'config/domain';

export const LENGHT_FIELD_VALIDATION: Record<string, number> = {
  prePersonalId: 6,
  postPersonalId: 4,
};

const useValidationSchema = () => {
  const { t } = useTranslation();

  const prePersonalIdRegexLength = new RegExp(
    `^\\d{LENGHT_VALUE_FIELD}$`.replace(
      'LENGHT_VALUE_FIELD',
      String(LENGHT_FIELD_VALIDATION.prePersonalId),
    ),
  );

  const postPersonalIdRegexLength = new RegExp(
    `^\\d{LENGHT_VALUE_FIELD}$`.replace(
      'LENGHT_VALUE_FIELD',
      String(LENGHT_FIELD_VALIDATION.postPersonalId),
    ),
  );

  return useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string()
          .trim()
          .matches(/^\p{L}+$/u, t('onlyLettersAllowed'))
          .required(t('requiredField')),
        lastName: Yup.string()
          .trim()
          .matches(/^\p{L}+$/u, t('onlyLettersAllowed'))
          .required(t('requiredField')),
        prePersonalId: Yup.string()
          .trim()
          .matches(prePersonalIdRegexLength, t('wrongFormat_personalId'))
          .test('too-old', t('tooOld'), (value, context) => {
            if (value === undefined) return true;
            const age = getAgeFromPersonalID(
              `${value}-${context.parent.postPersonalId}`,
            );
            return age < domainConfig.maxAge;
          })
          .test('too-young', t('tooYoung'), (value, context) => {
            if (value === undefined) return true;
            const age = getAgeFromPersonalID(
              `${value}-${context.parent.postPersonalId}`,
            );
            return age > domainConfig.minAge;
          })
          .test(
            'personal-id-format',
            t('wrongFormat_personalId'),
            (value, context) => {
              if (
                value === undefined ||
                context.parent.postPersonalId === undefined
              )
                return true;
              return validatePersonalID(
                `${value}-${context.parent.postPersonalId}`,
              );
            },
          )
          .required(t('requiredField')),
        postPersonalId: Yup.string()
          .trim()
          .matches(postPersonalIdRegexLength, t('wrongFormat_personalId'))
          .test(
            'personal-id-format',
            t('wrongFormat_personalId'),
            (value, context) => {
              if (
                value === undefined ||
                context.parent.prePersonalId === undefined
              )
                return true;
              return validatePersonalID(
                `${context.parent.prePersonalId}-${value}`,
              );
            },
          )
          .required(t('requiredField')),
        bankAccountClearingNumber: Yup.string()
          .min(4, t('clearingNumberTooShort'))
          .max(5, t('clearingNumberTooLong'))
          .matches(/^\d+$/, t('clearingOnlyDigits'))
          .required(t('requiredField')),
        bankAccountNumber: Yup.string()
          .min(7, t('bankAccountNumberTooShort'))
          .max(16, t('bankAccountNumberTooLong'))
          .matches(/^\d+$/, t('bankAccountNumberOnlyDigits'))
          .required(t('requiredField')),
        monthlyIncome: Yup.number().required(t('requiredField')),
        monthlyOtherLoans: Yup.number().required(t('requiredField')),
        monthlyHousingCosts: Yup.number().required(t('requiredField')),
        monthlyOtherLivingCosts: Yup.number().required(t('requiredField')),
        password: Yup.string()
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
            t('incorrectPasswordFormat'),
          )
          .required(t('requiredField')),
        // TODO add password strength validation
        phone: Yup.string()
          .trim()
          .matches(domainConfig.phoneNumberRegExp, t('wrongFormat_phone'))
          .required(t('requiredField')),
        email: Yup.string()
          .trim()
          .matches(domainConfig.emailRegExp, t('wrongFormat_email'))
          .required(t('requiredField')),
        numberOfAdultsInHouse: Yup.string().required(t('requiredField')),
        numberOfChildrenInHouse: Yup.string().required(t('requiredField')),
        agreement1: Yup.bool().isTrue(t('agreementRequired')),
        agreement2: Yup.bool(),
      }),
    [t],
  );
};

export default useValidationSchema;
